import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import PaymentezPaymentCurrentButton from './PaymentezPaymentCurrentButton';
import PaymentezPaymentDeferredButton from './PaymentezPaymentDeferredButton';
import PaymentezPaymentDeferredNoInterestButton from './PaymentezPaymentDeferredNoInterestButton';
import PaymentezCreditCards from './PaymentezCreditCards';
import PaymentezTransactionStatusError from './PaymentezTransactionStatusError';

function paymentezGetEnvMode() {
  switch (process.env.NODE_ENV) {
    case 'development':
      return 'stg';
    default:
      return 'prod';
  }
}

const PAYMENTEZ_CURRENT_PAYMENT = 0;
const PAYMENTEZ_DEFERRED_PAYMENT = 1;
const PAYMENTEZ_DEFERRED_WITH_INTERESTS_PAYMENT = 2;

const PAYMENTEZ_STATUS_SUCCESS = 'success';
const PAYMENTEZ_STATUS_FAILURE = 'failure';
const PAYMENTEZ_STATUS_PENDING = 'pending';

// Approved transaction
const PAYMENTEZ_APPROVED = 3;
// Denied transaction
const PAYMENTEZ_DENIED = 9;
// Reviewed transaction
const PAYMENTEZ_REVIEWED = 1;
// Rejected by fraud system transaction
const PAYMENTEZ_REJECTED = 11;
// Card in black list
const PAYMENTEZ_BLACKLIST_CARD = 12;

export default function Paymentez({ checkout, onPaymentAccepted }) {
  const [transactionStatusError, setTransactionStatusError] = useState(null);

  const paymentCurrentRef = useRef(null);
  const paymentDeferredRef = useRef(null);
  const paymentDeferredWithInterestsRef = useRef(null);

  const onPaymentezButtonTypeClick = useCallback(
    (paymentType) => {
      window.PaymentezCheckoutInstance.open({
        user_id: checkout.order.customer.customerId,
        user_email: checkout.order.billing.email,
        user_phone: checkout.order.billing.phone,
        order_amount: checkout.order.total,
        order_vat: checkout.order.totalTax,
        order_description: `Número de órden: ${checkout.order.orderNumber}`,
        order_reference: checkout.order.orderKey,
        order_installments_type: paymentType,
        order_taxable_amount: 0, // only applies for Ecuador
        order_tax_percentage: 0, // only applies for Ecuador
      });
    },
    [checkout]
  );

  const removeAllEventsBinds = useCallback(() => {
    paymentCurrentRef.current?.removeEventListener(
      'click',
      onPaymentezButtonTypeClick
    );

    paymentDeferredRef.current?.removeEventListener(
      'click',
      onPaymentezButtonTypeClick
    );

    paymentDeferredWithInterestsRef.current?.removeEventListener(
      'click',
      onPaymentezButtonTypeClick
    );
  }, [
    paymentCurrentRef,
    paymentDeferredRef,
    paymentDeferredWithInterestsRef,
    onPaymentezButtonTypeClick,
  ]);

  const onPaymentApprovedCallback = useCallback(
    (transactionId) => {
      onPaymentAccepted(transactionId);
    },
    [onPaymentAccepted]
  );

  const onPaymentFailureCallback = useCallback((statusDetail) => {
    let transactionStatusErrorMessage;

    switch (statusDetail) {
      case PAYMENTEZ_APPROVED: // This shouldn't happen in an error scenario
        transactionStatusErrorMessage = 'Su transacción fue aprobada.';

      case PAYMENTEZ_REVIEWED: // This shouldn't happen in an error scenario
        transactionStatusErrorMessage = 'Su transacción está en revisión.';
        break;

      case PAYMENTEZ_DENIED:
        transactionStatusErrorMessage =
          'Lo sentimos, la transacción ha sido denegada. ' +
          'Puede intentarlo nuevamente o usar otra tarjeta.';
        break;

      case PAYMENTEZ_REJECTED:
        transactionStatusErrorMessage =
          'Lo sentimos, la transacción ha sido rechazada. ' +
          'Por favor inténtelo nuevamente o use otra tarjeta.';
        break;

      case PAYMENTEZ_BLACKLIST_CARD:
        transactionStatusErrorMessage =
          'Lo sentimos, la transacción ha sido rechazada debido a que ' +
          'la tarjeta aparece en la lista de tarjetas prohibidas. ' +
          'Por favor inténtelo nuevamente o use otra tarjeta.';
        break;
    }

    setTransactionStatusError({
      detail: statusDetail,
      message: transactionStatusErrorMessage,
    });
  }, []);

  const onPaymentPendingCallback = useCallback(() => {
    console.log('__FIXME__: onPaymentPendingCallback()');
  }, []);

  useEffect(() => {
    if (
      paymentCurrentRef.current === null ||
      paymentDeferredRef.current === null ||
      paymentDeferredWithInterestsRef.current === null
    ) {
      return;
    }

    window.PaymentezCheckoutInstance = new PaymentezCheckout.modal({
      client_app_code: process.env.NEXT_PUBLIC_PAYMENTEZ_APP_CODE_CLIENT,
      client_app_key: process.env.NEXT_PUBLIC_PAYMENTEZ_APP_KEY_CLIENT,
      locale: 'es', // we preferred Spanish by default
      env_mode: paymentezGetEnvMode(),
      onResponse: function ({ transaction: { id, status, status_detail } }) {
        switch (status) {
          case PAYMENTEZ_STATUS_SUCCESS:
            onPaymentApprovedCallback(id);
            break;

          case PAYMENTEZ_STATUS_FAILURE:
            onPaymentFailureCallback(status_detail);
            break;

          // Pending should never happens since we doesn't have it enabled
          case PAYMENTEZ_STATUS_PENDING:
            onPaymentPendingCallback();
            break;
        }
      },
    });

    removeAllEventsBinds();

    paymentCurrentRef.current.addEventListener('click', () =>
      onPaymentezButtonTypeClick(PAYMENTEZ_CURRENT_PAYMENT)
    );

    paymentDeferredRef.current.addEventListener('click', () =>
      onPaymentezButtonTypeClick(PAYMENTEZ_DEFERRED_PAYMENT)
    );

    paymentDeferredWithInterestsRef.current.addEventListener('click', () =>
      onPaymentezButtonTypeClick(PAYMENTEZ_DEFERRED_WITH_INTERESTS_PAYMENT)
    );

    return removeAllEventsBinds;
  }, [
    paymentCurrentRef,
    paymentDeferredRef,
    paymentDeferredWithInterestsRef,
    onPaymentezButtonTypeClick,
    onPaymentApprovedCallback,
    onPaymentFailureCallback,
    onPaymentPendingCallback,
    removeAllEventsBinds,
  ]);

  const hasTransactionStatusError = transactionStatusError !== null;

  return (
    <div className="flex flex-col w-full">
      <div className="flex-1 py-8 px-10 space-y-6 overflow-y-auto">
        {hasTransactionStatusError && (
          <PaymentezTransactionStatusError {...transactionStatusError} />
        )}
        <div className="grid grid-cols-1 gap-6">
          <PaymentezPaymentCurrentButton ref={paymentCurrentRef} />
          <PaymentezPaymentDeferredButton ref={paymentDeferredRef} />
          <PaymentezPaymentDeferredNoInterestButton
            ref={paymentDeferredWithInterestsRef}
          />
        </div>
      </div>
      <div className="flex items-center w-full border-t border-black border-opacity-20 px-10 py-8 bg-white">
        <div className="flex-1 flex justify-center items-center space-x-4">
          <div className="text-sm texrt-gray-500">
            Aceptamos las siguientes tarjetas
          </div>
          <PaymentezCreditCards />
        </div>
      </div>
    </div>
  );
}

Paymentez.defaultProps = {
  onPaymentCanceled: () => null,
};

export const PaymentezCheckoutShape = PropTypes.shape({
  order: PropTypes.shape({
    customer: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      email: PropTypes.string,
      phone: PropTypes.string,
      total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      totalTax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      orderNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      orderKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
}).isRequired;

Paymentez.propTypes = {
  checkout: PaymentezCheckoutShape,
  onPaymentCanceled: PropTypes.func,
};
